import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDeviceCatalog, type DeviceCatalogEntry } from '@/api/deviceCatalog';

/**
 * The device catalog, with a built-in answer for every screen that draws a
 * device name or icon.
 *
 * Rule: the catalog may be slow, may fail, may not have been edited at all —
 * and a device card must never come out blank because of that. So this hook
 * only ever ADDS to what the screen already knows: `labelFor` and `iconFor`
 * take the caller's own built-in as the fallback and return it untouched
 * whenever the catalog has nothing better.
 */
export const DEVICE_CATALOG_QUERY_KEY = ['device-catalog'] as const;

export interface DeviceCatalogView {
   entries: DeviceCatalogEntry[];
   byId: Map<string, DeviceCatalogEntry>;
   /** The admin's name for this device, else the caller's built-in. */
   labelFor: (id: string | undefined, fallback: string) => string;
   /** An uploaded icon URL, or null to draw the caller's own glyph. */
   iconFor: (id: string | undefined) => string | null;
   isLoading: boolean;
}

export function useDeviceCatalog(): DeviceCatalogView {
   const { data, isLoading } = useQuery({
      queryKey: DEVICE_CATALOG_QUERY_KEY,
      queryFn: getDeviceCatalog,
      // Names and icons change about once a quarter; re-reading them on every
      // screen mount would be traffic for nothing.
      staleTime: 5 * 60_000,
      retry: 1,
   });
   // Stable identities: a fleet list memoises on these, so rebuilding them
   // every render would re-map every row on every keystroke elsewhere.
   const entries = useMemo(() => data ?? [], [data]);
   const byId = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
   const labelFor = useCallback(
      (id: string | undefined, fallback: string) => (id && byId.get(id)?.label) || fallback,
      [byId]
   );
   const iconFor = useCallback(
      (id: string | undefined) => (id && byId.get(id)?.iconUrl) || null,
      [byId]
   );
   return { entries, byId, labelFor, iconFor, isLoading };
}
